import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styles from "../../styles/components/NotificationsModal.module.css";

import ModalOverlay from "../../ui/Modals/ModalOverlay";
import SearchInput from "../../ui/inputs/SearchInput";
import DropdownSelectPill from "../../ui/inputs/DropdownSelectPill";
import ChoiceChip from "../../ui/selectors/ChoiceChip";
import Button from "../../ui/buttons/Button";
import LoadingScreen from "../../ui/loadingScreen/LoadingScreen";
import NotificationRow from "./NotificationRow";
import { useModalFocusTrap } from "../mailContent/useModalFocusTrap";

import {
  useIncomingNotifications,
  useNotifications,
} from "../../contexts/NotificationsContext";
import useWhatsAppEnabled from "../../hooks/useWhatsAppEnabled";
import { notificationsService } from "../../services/notifications.service";

import {
  getAvailableFilters,
  getFilterTypeCodes,
  groupByDay,
  matchesFilter,
  matchesSearch,
} from "./notificationsMeta";

import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";

const PAGE_SIZE = 50;

const READ_FILTER_OPTIONS = [
  { label: "Todos", onlyUnread: false },
  { label: "Sin leer", onlyUnread: true },
];

const NotificationsModal = ({ onClose, onOpenNotification }) => {
  const { unreadCount, markAllAsRead } = useNotifications();
  const subscribeToIncoming = useIncomingNotifications();
  const whatsappEnabled = useWhatsAppEnabled();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);

  const [onlyUnread, setOnlyUnread] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [markingAll, setMarkingAll] = useState(false);

  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const requestGenerationRef = useRef(0);

  useModalFocusTrap(dialogRef, closeButtonRef, onClose);

  const filters = useMemo(
    () => getAvailableFilters({ whatsappEnabled }),
    [whatsappEnabled],
  );

  const loadNotifications = useCallback(
    async ({ offset = 0 } = {}) => {
      const isFirstPage = offset === 0;
      requestGenerationRef.current += 1;
      const generation = requestGenerationRef.current;

      if (isFirstPage) {
        setLoading(true);
        setError("");
      } else {
        setLoadingMore(true);
      }

      try {
        const data = await notificationsService.list({
          isRead: onlyUnread ? false : undefined,
          typeCodes: getFilterTypeCodes(typeFilter),
          limit: PAGE_SIZE,
          offset,
        });
        if (generation !== requestGenerationRef.current) return;

        const results = data?.results || [];
        setNotifications((previous) =>
          isFirstPage ? results : [...previous, ...results],
        );
        setHasMore(Boolean(data?.next));
      } catch (requestError) {
        if (generation !== requestGenerationRef.current) return;
        console.error("Error cargando notificaciones:", requestError);
        if (isFirstPage) setError("No pudimos cargar las notificaciones.");
      } finally {
        if (generation === requestGenerationRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [onlyUnread, typeFilter],
  );

  useEffect(() => {
    loadNotifications({ offset: 0 });
  }, [loadNotifications]);

  useEffect(() => {
    if (!subscribeToIncoming) return undefined;

    return subscribeToIncoming((incoming) => {
      if (!incoming?.id) return;
      if (!matchesFilter(incoming, typeFilter)) return;
      if (onlyUnread && incoming.is_read) return;

      setNotifications((previous) =>
        previous.some((item) => item.id === incoming.id)
          ? previous
          : [incoming, ...previous],
      );
    });
  }, [subscribeToIncoming, typeFilter, onlyUnread]);

  const visibleNotifications = useMemo(
    () =>
      notifications.filter((notification) =>
        matchesSearch(notification, search),
      ),
    [notifications, search],
  );

  const groups = useMemo(
    () => groupByDay(visibleNotifications),
    [visibleNotifications],
  );

  const handleOpen = useCallback(
    (notification) => {
      setNotifications((previous) =>
        previous.map((item) =>
          item.id === notification.id ? { ...item, is_read: true } : item,
        ),
      );
      onOpenNotification?.(notification);
    },
    [onOpenNotification],
  );

  const handleMarkAll = useCallback(async () => {
    if (markingAll) return;
    setMarkingAll(true);
    try {
      await markAllAsRead();
      if (onlyUnread) {
        setNotifications([]);
        setHasMore(false);
      } else {
        setNotifications((previous) =>
          previous.map((item) => ({ ...item, is_read: true })),
        );
      }
    } catch (requestError) {
      console.error("Error marcando todas las notificaciones como leídas:", requestError);
    } finally {
      setMarkingAll(false);
    }
  }, [markingAll, markAllAsRead, onlyUnread]);

  const emptyMessage = search
    ? "Ninguna notificación cargada coincide con la búsqueda."
    : onlyUnread
      ? "No tenés notificaciones sin leer."
      : "No tenés notificaciones.";

  const loadMoreButton = hasMore ? (
    <div className={styles.loadMore}>
      <Button
        variant="secondary"
        tone="neutral"
        height={40}
        disabled={loadingMore}
        onClick={() => loadNotifications({ offset: notifications.length })}
      >
        {loadingMore ? "Cargando..." : "Cargar anteriores"}
      </Button>
    </div>
  ) : null;

  return (
    <ModalOverlay className={styles.overlay} onClose={onClose}>
      <div
        ref={dialogRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notifications-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2 id="notifications-modal-title" className={styles.title}>
              Notificaciones
            </h2>
            {unreadCount > 0 ? (
              <span className={styles.subtitle}>{unreadCount} sin leer</span>
            ) : null}
          </div>

          <button
            ref={closeButtonRef}
            type="button"
            className={styles.closeButton}
            aria-label="Cerrar"
            onClick={onClose}
          >
            <CrossIcon className={styles.closeIcon} aria-hidden="true" focusable="false" />
          </button>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.toolbarRow}>
            <div className={styles.search}>
              <SearchInput
                onSearch={setSearch}
                placeholder="Lead, empresa o asunto..."
                width="100%"
                height={40}
              />
            </div>

            <DropdownSelectPill
              aria-label="Tipo de notificación"
              options={filters.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              value={typeFilter}
              onChange={setTypeFilter}
              chevronSize={16}
              width={220}
              height={40}
              menuMinWidth={220}
              style={{
                backgroundColor: "var(--surface-input-hover)",
                color: "var(--text-primary)",
              }}
            />
          </div>

          <div className={styles.toolbarRow}>
            <div className={styles.readFilter} role="group" aria-label="Estado de lectura">
              {READ_FILTER_OPTIONS.map((option) => (
                <ChoiceChip
                  key={option.label}
                  label={option.label}
                  selected={onlyUnread === option.onlyUnread}
                  onClick={() => setOnlyUnread(option.onlyUnread)}
                  height={32}
                  iconSize={0}
                  fontWeight={500}
                  unselectedBackground="transparent"
                />
              ))}
            </div>

            <Button
              className={styles.markAll}
              variant="secondary"
              tone="neutral"
              height={40}
              disabled={markingAll || unreadCount < 1}
              onClick={handleMarkAll}
            >
              {markingAll ? "Marcando..." : "Marcar todo como leído"}
            </Button>
          </div>
        </div>

        <div className={styles.body}>
          {loading ? (
            <LoadingScreen embedded mainText="Cargando notificaciones..." subText="" />
          ) : error ? (
            <div className={styles.stateBox}>
              <span>{error}</span>
              <Button
                variant="secondary"
                tone="neutral"
                height={40}
                onClick={() => loadNotifications({ offset: 0 })}
              >
                Reintentar
              </Button>
            </div>
          ) : !visibleNotifications.length ? (
            <div className={styles.stateBox}>
              <span>{emptyMessage}</span>
              {loadMoreButton}
            </div>
          ) : (
            <div className={styles.list}>
              {groups.map((group) => (
                <section key={group.key} className={styles.dayGroup}>
                  <div className={styles.dayHeader}>
                    <span className={styles.dayLabel}>{group.label}</span>
                    {group.unreadCount > 0 ? (
                      <span className={styles.dayCount}>
                        {group.unreadCount} sin leer
                      </span>
                    ) : null}
                  </div>

                  {group.items.map((notification) => (
                    <NotificationRow
                      key={notification.id}
                      notification={notification}
                      onOpen={handleOpen}
                    />
                  ))}
                </section>
              ))}

              {loadMoreButton}
            </div>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
};

export default NotificationsModal;
